import { graphqlFetch } from '@pylot-data/graphqlFetch'
import {
    associatedProductsConfigQuery,
    AssociatedProductsConfigFields,
    AssociatedProductsConfigOutput
} from '@pylot-data/api/operations/graphql/associatedProductsConfigQuery'

/**
 * EVO-263: tope de miniaturas de color visibles y titulo por defecto del
 * bloque, configurables por store view desde el admin de Magento.
 *
 * Se resuelve en runtime (no por `preload`) copiando el patron de
 * `low-stock/getSalesUnitsConfig`: una sola promesa memoizada por sesion, con
 * fallback silencioso si el edge todavia no expone los campos.
 */

/** Contextos que tienen tope propio en la config. */
export type AssociatedProductsContext = 'plp' | 'srp' | 'pdp'

/**
 * Tope usado cuando la config no llega (o llega en 0 / vacia / negativa).
 * Acordado con el cliente: 4 miniaturas + el recuadro "+N".
 */
export const ASSOCIATED_PRODUCTS_FALLBACK_LIMIT = 4

export type AssociatedProductsConfig = {
    limits: Record<AssociatedProductsContext, number>
    /** `associated_products_default_title`; '' cuando no viene. */
    defaultTitle: string
}

export const ASSOCIATED_PRODUCTS_CONFIG_FALLBACK: AssociatedProductsConfig = {
    limits: {
        plp: ASSOCIATED_PRODUCTS_FALLBACK_LIMIT,
        srp: ASSOCIATED_PRODUCTS_FALLBACK_LIMIT,
        pdp: ASSOCIATED_PRODUCTS_FALLBACK_LIMIT
    },
    defaultTitle: ''
}

/**
 * Un tope solo se respeta si es un entero positivo: `0`, negativos, `null`,
 * `NaN` o strings sin numero caen al fallback en vez de romper el render (o de
 * dejar la fila vacia). El edge ya castea a `Int`, pero se acepta string por si
 * algun ambiente devuelve el valor crudo de `core_config_data`.
 */
export const normalizeAssociatedProductsLimit = (
    value?: number | string | null,
    fallback: number = ASSOCIATED_PRODUCTS_FALLBACK_LIMIT
): number => {
    const parsed = typeof value === 'string' ? Number(value) : value

    if (typeof parsed !== 'number' || !Number.isFinite(parsed) || parsed <= 0) {
        return fallback
    }

    return Math.floor(parsed)
}

/**
 * Lee `config.base.associated_products` (path confirmado con el edge). Si el
 * grupo no viene — modulo de Magento no deployado en el ambiente, o query
 * fallida — devuelve el fallback completo en vez de romper el render.
 */
export const normalizeAssociatedProductsConfig = (
    response?: AssociatedProductsConfigOutput | null
): AssociatedProductsConfig => {
    const group: AssociatedProductsConfigFields =
        response?.config?.base?.associated_products ?? {}

    const title = group.associated_products_default_title ?? ''

    return {
        limits: {
            plp: normalizeAssociatedProductsLimit(
                group.associated_products_plp_limit
            ),
            srp: normalizeAssociatedProductsLimit(
                group.associated_products_srp_limit
            ),
            pdp: normalizeAssociatedProductsLimit(
                group.associated_products_pdp_limit
            )
        },
        defaultTitle: typeof title === 'string' ? title.trim() : ''
    }
}

let associatedProductsConfigPromise: Promise<AssociatedProductsConfig> | null =
    null

export const getAssociatedProductsConfig =
    async (): Promise<AssociatedProductsConfig> => {
        if (!associatedProductsConfigPromise) {
            associatedProductsConfigPromise = graphqlFetch<
                Record<string, never>,
                AssociatedProductsConfigOutput
            >({
                query: associatedProductsConfigQuery
            })
                .then(({ data }) => normalizeAssociatedProductsConfig(data))
                .catch(() => ASSOCIATED_PRODUCTS_CONFIG_FALLBACK)
        }

        return associatedProductsConfigPromise
    }

/** Solo para tests: limpia la promesa memoizada. */
export const resetAssociatedProductsConfigCache = (): void => {
    associatedProductsConfigPromise = null
}
